import { useMemo, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSupportTicketMessageThread } from "@/components/HubSupportTicketMessageThread";
import { MrpSkuHoverPeek, useDeferredMrpSkuHoverHints } from "@/components/mrp/MrpSkuHoverPeek";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import {
  documentRequestKindLabel,
  documentRequestMessagesAsHubThread,
  documentRequestPriorityLabel,
  documentRequestStatusLabel,
  documentRequestSummary,
  fetchDocumentRequest,
  fetchMyDocumentRequests,
  patchDocumentRequest,
  postDocumentRequestMessage,
  type DocumentRequestMessage,
  type DocumentRequestRow,
  type DocumentRequestStatus,
} from "@/lib/documentRequest";
import { formatTimestamp } from "@/lib/utils";

const PAGE_HELP =
  "Requests you submitted for controlled document access or for a missing document on a part number. Document Control staff respond in the request thread.";

function documentRequestStatusTone(
  status: DocumentRequestStatus,
): "warning" | "neutral" | "success" | "danger" {
  if (status === "pending") return "warning";
  if (status === "investigating") return "neutral";
  if (status === "granted" || status === "fulfilled") return "success";
  if (status === "denied") return "danger";
  return "neutral";
}

function DocumentRequestStatusBadge({ status }: { status: DocumentRequestStatus }): JSX.Element {
  return <Badge tone={documentRequestStatusTone(status)}>{documentRequestStatusLabel(status)}</Badge>;
}

function DocumentRequestListPage(): JSX.Element {
  const listQuery = useQuery({
    queryKey: ["document-requests", "mine"],
    queryFn: fetchMyDocumentRequests,
  });
  const rows = listQuery.data?.rows ?? [];
  const queueSkus = useMemo(() => rows.map((row) => row.sku), [rows]);
  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    queueSkus,
    "my-document-requests",
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageTitleWithHelp hoverHint={PAGE_HELP}>My document requests</PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Your requests</CardTitle>
        </CardHeader>
        <CardBody className="p-0 sm:p-0">
          {listQuery.isLoading ? (
            <p className="px-5 py-4 text-sm text-slate-500">Loading your requests…</p>
          ) : listQuery.isError ? (
            <p className="px-5 py-4 text-sm text-red-600">{(listQuery.error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-600">
              You have not submitted any document requests yet. Use Request access or Request a document on a part
              number&apos;s Related documents tab.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((row) => (
                <li key={row.id}>
                  <Link
                    to={`/personal/document-requests/${encodeURIComponent(row.id)}`}
                    className="flex flex-col gap-2 px-5 py-4 transition-colors hover:bg-slate-50"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">Request #{row.id}</span>
                      <MrpSkuHoverPeek
                        sku={row.sku}
                        hint={skuHoverHints?.[row.sku.trim()]}
                        hintsLoading={hintsLoading}
                        onHoverIntent={enableHintsFetch}
                      />
                      <DocumentRequestStatusBadge status={row.status} />
                      <span className="text-xs text-slate-500">{documentRequestKindLabel(row.kind)}</span>
                    </div>
                    <p className="line-clamp-2 text-sm text-slate-700">{documentRequestSummary(row)}</p>
                    <p className="text-xs text-slate-500">Submitted {formatTimestamp(row.created_at)}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function DocumentRequestDetailPage(): JSX.Element {
  const { id: requestId = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [replyDraft, setReplyDraft] = useState("");
  const [replyError, setReplyError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ["document-requests", "mine", "detail", requestId],
    queryFn: () => fetchDocumentRequest(requestId),
    enabled: requestId !== "",
  });

  const cancelMutation = useMutation({
    mutationFn: () => patchDocumentRequest(requestId, { status: "cancelled" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-requests"] });
      navigate("/personal/document-requests");
    },
  });

  const replyMutation = useMutation({
    mutationFn: (body: string) => postDocumentRequestMessage(requestId, body),
    onSuccess: (result) => {
      setReplyDraft("");
      setReplyError(null);
      queryClient.setQueryData(
        ["document-requests", "mine", "detail", requestId],
        (previous: { request: DocumentRequestRow; messages: DocumentRequestMessage[] } | undefined) =>
          previous ? { ...previous, messages: result.messages } : previous,
      );
      void queryClient.invalidateQueries({ queryKey: ["document-requests", "mine"] });
    },
    onError: (error: Error) => {
      setReplyError(error.message);
    },
  });

  const request = detailQuery.data?.request;
  const messages = detailQuery.data?.messages ?? [];
  const canReply =
    request != null && request.status !== "granted" && request.status !== "fulfilled" && request.status !== "denied" && request.status !== "cancelled";

  const detailSkus = useMemo(() => (request?.sku != null ? [request.sku] : []), [request?.sku]);
  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    detailSkus,
    "my-document-request-detail",
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={() => navigate("/personal/document-requests")}>
          Back to list
        </Button>
        <PageTitleWithHelp hoverHint={PAGE_HELP}>
          {request ? `Request #${request.id}` : "Document request"}
        </PageTitleWithHelp>
      </header>

      <Card>
        <CardBody className="space-y-5">
          {detailQuery.isLoading && !request ? (
            <p className="text-sm text-slate-500">Loading request…</p>
          ) : detailQuery.isError ? (
            <p className="text-sm text-red-600">{(detailQuery.error as Error).message}</p>
          ) : request ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <DocumentRequestStatusBadge status={request.status} />
                <span className="text-sm text-slate-600">{documentRequestKindLabel(request.kind)}</span>
                <MrpSkuHoverPeek
                  sku={request.sku}
                  hint={skuHoverHints?.[request.sku.trim()]}
                  hintsLoading={hintsLoading}
                  onHoverIntent={enableHintsFetch}
                  tab="documents"
                  className="text-sm"
                />
                <span className="text-xs text-slate-500">Submitted {formatTimestamp(request.created_at)}</span>
              </div>
              {request.resolution_note?.trim() ? (
                <div className="rounded-md border border-emerald-100 bg-emerald-50/80 p-3">
                  <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-emerald-800">
                    Resolution
                  </span>
                  <p className="whitespace-pre-wrap text-sm text-emerald-950">{request.resolution_note}</p>
                </div>
              ) : null}
              {request.kind === "new_document" ? (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-700">
                  {request.priority ? (
                    <span>
                      <span className="font-medium text-slate-600">Priority:</span>{" "}
                      {documentRequestPriorityLabel(request.priority)}
                    </span>
                  ) : null}
                  {request.requested_by_date ? (
                    <span>
                      <span className="font-medium text-slate-600">Request by:</span> {request.requested_by_date}
                    </span>
                  ) : null}
                  {request.document_kind_label ? (
                    <span>
                      <span className="font-medium text-slate-600">Document type:</span>{" "}
                      {request.document_kind_label}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div>
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Request</span>
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-800">
                  {request.body}
                </pre>
              </div>
              <HubSupportTicketMessageThread
                messages={documentRequestMessagesAsHubThread(messages)}
                draft={replyDraft}
                onDraftChange={setReplyDraft}
                submitting={replyMutation.isPending}
                canReply={canReply}
                error={replyError}
                onSubmit={() => {
                  const trimmed = replyDraft.trim();
                  if (trimmed === "") return;
                  replyMutation.mutate(trimmed);
                }}
              />
              {request.status === "pending" || request.status === "investigating" ? (
                <div className="flex justify-end border-t border-slate-100 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={cancelMutation.isPending}
                    onClick={() => {
                      void cancelMutation.mutateAsync();
                    }}
                  >
                    {cancelMutation.isPending ? "Cancelling…" : "Cancel request"}
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-slate-600">Request not found.</p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export function MyDocumentRequestsPage(): JSX.Element {
  return (
    <Routes>
      <Route index element={<DocumentRequestListPage />} />
      <Route path=":id" element={<DocumentRequestDetailPage />} />
      <Route path="*" element={<Navigate to="/personal/document-requests" replace />} />
    </Routes>
  );
}
